// Minimal Supabase-compatible client backed by a TrialStore JSON envelope in
// localStorage. Implements only the patterns the codebase actually uses:
// chainable .from(table).{select,insert,update,upsert,delete}() with the
// standard filter chain, the handful of foreign-table embed strings that
// appear in the app, basic storage (data-URL based), and stubbed auth.

import { saveTrialStore, clearTrialStore, nextId } from "./TrialStore";

// Foreign-table relationships used in select() embed strings. Keyed by parent
// table; each entry maps the relation name (as it appears in select string)
// to the child table plus the join key.
const RELATIONS = {
  memberships: {
    orgs: { table: "orgs", many: false, parentFk: "org_id", childKey: "id" },
    people: { table: "people", many: false, parentFk: "person_id", childKey: "id" },
  },
  people: {
    days: { table: "days", many: true, parentKey: "id", childFk: "person_id" },
  },
  days: {
    day_type: { table: "day_types", many: false, parentFk: "day_type", childKey: "id" },
  },
  projects: {
    settings: { table: "settings", many: true, parentKey: "id", childFk: "project_id" },
  },
};

// Parse a select string like "*, people(*, days(*, day_type(*)))" into a tree.
// Returns { cols: ["*"|...], embeds: [{ name, cols, embeds }] }.
function parseSelect(selectStr) {
  if (!selectStr || selectStr === "*") return { cols: ["*"], embeds: [] };
  const tokens = [];
  let buf = "";
  let depth = 0;
  for (const ch of selectStr) {
    if (ch === "(") {
      depth++;
      buf += ch;
    } else if (ch === ")") {
      depth--;
      buf += ch;
    } else if (ch === "," && depth === 0) {
      tokens.push(buf.trim());
      buf = "";
    } else {
      buf += ch;
    }
  }
  if (buf.trim()) tokens.push(buf.trim());

  const cols = [];
  const embeds = [];
  for (const tok of tokens) {
    const parenIdx = tok.indexOf("(");
    if (parenIdx === -1) {
      cols.push(tok);
    } else {
      const name = tok.slice(0, parenIdx).trim();
      const inner = tok.slice(parenIdx + 1, tok.lastIndexOf(")"));
      const childTree = parseSelect(inner);
      embeds.push({ name, ...childTree });
    }
  }
  return { cols, embeds };
}

function passesPredicate(row, pred) {
  const v = row?.[pred.col];
  switch (pred.op) {
    case "eq":
      return v === pred.value;
    case "neq":
      return v !== pred.value;
    case "gt":
      return v > pred.value;
    case "gte":
      return v >= pred.value;
    case "lt":
      return v < pred.value;
    case "lte":
      return v <= pred.value;
    case "in":
      return Array.isArray(pred.value) && pred.value.includes(v);
    case "is":
      if (pred.value === null) return v === null || v === undefined;
      return v === pred.value;
    case "not_is":
      if (pred.value === null) return v !== null && v !== undefined;
      return v !== pred.value;
    case "or": {
      return pred.clauses.some((c) => passesPredicate(row, c));
    }
    default:
      return true;
  }
}

// Parse "user_id.eq.5,user_id.is.null" into an array of predicates.
function parseOrString(orString) {
  return orString.split(",").map((clause) => {
    const parts = clause.split(".");
    const col = parts[0];
    const op = parts[1];
    let raw = parts.slice(2).join(".");
    let value;
    if (raw === "null") value = null;
    else if (raw === "true") value = true;
    else if (raw === "false") value = false;
    else if (/^-?\d+(\.\d+)?$/.test(raw)) value = Number(raw);
    else value = raw;
    return { col, op, value };
  });
}

function applyOrder(rows, order) {
  if (!order) return rows;
  const sorted = [...rows].sort((a, b) => {
    const av = a?.[order.col];
    const bv = b?.[order.col];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return order.ascending ? -1 : 1;
    if (av > bv) return order.ascending ? 1 : -1;
    return 0;
  });
  return sorted;
}

function filterRows(rows, predicates) {
  return rows.filter((r) => predicates.every((p) => passesPredicate(r, p)));
}

// Walk a row through an embed tree, attaching child arrays/objects in place.
// `nestedFilters` and `nestedOrders` are keyed by foreignTable name (the path
// like "people.days") and applied to the appropriate level.
function expandEmbeds(state, table, rows, embeds, path, nestedFilters, nestedOrders) {
  if (!embeds || !embeds.length) return rows;
  return rows.map((row) => {
    const out = { ...row };
    for (const embed of embeds) {
      const rel = RELATIONS[table]?.[embed.name];
      if (!rel) continue; // unknown relation; ignore
      const childTable = rel.table;
      const childRows = state[childTable] || [];
      const subPath = path ? `${path}.${embed.name}` : embed.name;

      let resolved;
      if (rel.many) {
        resolved = childRows.filter((c) => c[rel.childFk] === row[rel.parentKey]);
      } else {
        const fkVal = row[rel.parentFk];
        const match = childRows.find((c) => c[rel.childKey] === fkVal);
        resolved = match ? [match] : [];
      }

      // Apply nested filters that target this embed level.
      const preds = nestedFilters[subPath] || [];
      if (preds.length) resolved = filterRows(resolved, preds);

      // Apply nested order that targets this embed level.
      const order = nestedOrders[subPath];
      if (order) resolved = applyOrder(resolved, order);

      // Recurse into deeper embeds.
      const expanded = expandEmbeds(state, childTable, resolved, embed.embeds, subPath, nestedFilters, nestedOrders);

      out[embed.name] = rel.many ? expanded : expanded[0] || null;
    }
    return out;
  });
}

class QueryBuilder {
  constructor(state, persist, table) {
    this.state = state;
    this.persist = persist;
    this.table = table;
    this.op = null;
    this.payload = null;
    this.selectStr = "*";
    this.parsedSelect = { cols: ["*"], embeds: [] };
    this.rootFilters = [];
    this.nestedFilters = {}; // { "people.days": [pred,...] }
    this.rootOrder = null;
    this.nestedOrders = {}; // { "people.days": order }
    this.rootLimit = null;
    this.singleMode = null; // 'single' | 'maybeSingle' | null
    this.returnRows = false; // toggled by .select() after insert/update/upsert
  }

  // ---- action setters ----

  select(selectStr) {
    if (this.op === null) {
      this.op = "select";
    } else {
      this.returnRows = true;
    }
    if (selectStr) {
      this.selectStr = selectStr;
      this.parsedSelect = parseSelect(selectStr);
    }
    return this;
  }

  insert(payload) {
    this.op = "insert";
    this.payload = payload;
    return this;
  }

  update(patch) {
    this.op = "update";
    this.payload = patch;
    return this;
  }

  delete() {
    this.op = "delete";
    return this;
  }

  upsert(payload) {
    this.op = "upsert";
    this.payload = payload;
    return this;
  }

  // ---- filter chain ----

  _addPred(pred, opts) {
    if (opts?.foreignTable) {
      const key = opts.foreignTable;
      if (!this.nestedFilters[key]) this.nestedFilters[key] = [];
      this.nestedFilters[key].push(pred);
    } else {
      this.rootFilters.push(pred);
    }
    return this;
  }

  eq(col, value) {
    return this._addPred({ col, op: "eq", value });
  }
  neq(col, value) {
    return this._addPred({ col, op: "neq", value });
  }
  gt(col, value) {
    return this._addPred({ col, op: "gt", value });
  }
  gte(col, value) {
    return this._addPred({ col, op: "gte", value });
  }
  lt(col, value) {
    return this._addPred({ col, op: "lt", value });
  }
  lte(col, value) {
    return this._addPred({ col, op: "lte", value });
  }
  in(col, arr) {
    return this._addPred({ col, op: "in", value: arr });
  }
  is(col, value) {
    return this._addPred({ col, op: "is", value });
  }
  not(col, op, value) {
    // Only "is" is observed: .not("date", "is", null) means date IS NOT NULL.
    if (op === "is") {
      return this._addPred({ col, op: "not_is", value });
    }
    // Generic fallback: invert the named operator.
    return this._addPred({ col, op: `not_${op}`, value });
  }
  or(filterString, opts) {
    const clauses = parseOrString(filterString);
    return this._addPred({ op: "or", clauses }, opts);
  }
  order(col, opts) {
    const spec = { col, ascending: opts?.ascending !== false };
    if (opts?.foreignTable) this.nestedOrders[opts.foreignTable] = spec;
    else this.rootOrder = spec;
    return this;
  }
  limit(n) {
    this.rootLimit = n;
    return this;
  }
  single() {
    this.singleMode = "single";
    return this;
  }
  maybeSingle() {
    this.singleMode = "maybeSingle";
    return this;
  }

  // ---- execution ----

  _shape(data) {
    if (this.singleMode === "single") {
      if (!data || data.length === 0) {
        return { data: null, error: { code: "PGRST116", message: "no rows" } };
      }
      return { data: data[0], error: null };
    }
    if (this.singleMode === "maybeSingle") {
      return { data: data && data.length ? data[0] : null, error: null };
    }
    return { data, error: null };
  }

  _runSelect() {
    const rows = this.state[this.table] || [];
    let filtered = filterRows(rows, this.rootFilters);
    filtered = applyOrder(filtered, this.rootOrder);
    if (this.rootLimit != null) filtered = filtered.slice(0, this.rootLimit);
    const expanded = expandEmbeds(
      this.state,
      this.table,
      filtered,
      this.parsedSelect.embeds,
      "",
      this.nestedFilters,
      this.nestedOrders
    );
    return this._shape(expanded);
  }

  _runInsert() {
    const incoming = Array.isArray(this.payload) ? this.payload : [this.payload];
    const inserted = [];
    if (!this.state[this.table]) this.state[this.table] = [];
    for (const row of incoming) {
      const newRow = { ...row };
      if (newRow.id === undefined || newRow.id === null) newRow.id = nextId(this.state);
      if (newRow.created_at === undefined && this.table !== "people") newRow.created_at = new Date().toISOString();
      this.state[this.table].push(newRow);
      inserted.push(newRow);
    }
    this.persist();
    if (!this.returnRows) {
      return { data: null, error: null };
    }
    return this._shape(inserted);
  }

  _runUpdate() {
    const rows = this.state[this.table] || [];
    const matches = filterRows(rows, this.rootFilters);
    for (const row of matches) {
      Object.assign(row, this.payload);
    }
    this.persist();
    if (!this.returnRows) return { data: null, error: null };
    return this._shape(matches);
  }

  _runDelete() {
    const rows = this.state[this.table] || [];
    const remove = new Set(filterRows(rows, this.rootFilters));
    this.state[this.table] = rows.filter((r) => !remove.has(r));
    this.persist();
    return { data: Array.from(remove), error: null };
  }

  _runUpsert() {
    // Match existing rows by id (the only conflict target used in app code).
    const incoming = Array.isArray(this.payload) ? this.payload : [this.payload];
    if (!this.state[this.table]) this.state[this.table] = [];
    const out = [];
    for (const row of incoming) {
      let existing = null;
      if (row.id !== undefined && row.id !== null) {
        existing = this.state[this.table].find((r) => r.id === row.id);
      }
      // Also support upserts that match by a unique-by-app key (settings,
      // hideGottaGoal uses upsert with a name+project_id+user_id triple) —
      // detect by checking for an exact (name, project_id, user_id) match
      // when an id was not supplied.
      if (!existing && this.table === "settings" && row.name) {
        existing = this.state.settings.find(
          (r) =>
            r.name === row.name &&
            r.project_id === row.project_id &&
            (r.user_id ?? null) === (row.user_id ?? null)
        );
      }
      if (existing) {
        Object.assign(existing, row);
        out.push(existing);
      } else {
        const newRow = { ...row };
        if (newRow.id === undefined || newRow.id === null) newRow.id = nextId(this.state);
        if (newRow.created_at === undefined) newRow.created_at = new Date().toISOString();
        this.state[this.table].push(newRow);
        out.push(newRow);
      }
    }
    this.persist();
    if (!this.returnRows) return { data: null, error: null };
    return this._shape(out);
  }

  _execute() {
    try {
      switch (this.op) {
        case "select":
          return this._runSelect();
        case "insert":
          return this._runInsert();
        case "update":
          return this._runUpdate();
        case "delete":
          return this._runDelete();
        case "upsert":
          return this._runUpsert();
        default:
          return { data: null, error: { message: "no op set" } };
      }
    } catch (err) {
      console.warn("MockSupabase error:", err);
      return { data: null, error: { message: err.message || String(err), code: "TRIAL_ERROR" } };
    }
  }

  // Make the builder thenable so `await supabase.from(...).select()...` works.
  then(resolve, reject) {
    try {
      const result = this._execute();
      resolve(result);
    } catch (err) {
      if (reject) reject(err);
      else throw err;
    }
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (typeof file === "string") {
      resolve(file);
      return;
    }
    if (file && typeof file.arrayBuffer === "function") {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
      return;
    }
    reject(new Error("unsupported upload payload"));
  });
}

function makeStorageBucket(state, persist, bucket) {
  return {
    async upload(path, file) {
      try {
        const dataUrl = await fileToDataUrl(file);
        if (!state.uploads) state.uploads = {};
        state.uploads[`${bucket}/${path}`] = {
          dataUrl,
          mimeType: file && file.type ? file.type : "application/octet-stream",
          size: file && file.size ? file.size : dataUrl.length,
          createdAt: new Date().toISOString(),
        };
        const save = persist();
        if (save && save.ok === false && save.code === "QUOTA") {
          delete state.uploads[`${bucket}/${path}`];
          return { data: null, error: { message: "image too large for trial", code: "TRIAL_QUOTA" } };
        }
        return { data: { path }, error: null };
      } catch (err) {
        return { data: null, error: { message: err.message || String(err), code: "TRIAL_UPLOAD" } };
      }
    },
    async createSignedUrl(path, _ttl) {
      const entry = state.uploads?.[`${bucket}/${path}`];
      if (!entry) return { data: null, error: { message: "not found" } };
      return { data: { signedUrl: entry.dataUrl }, error: null };
    },
    async remove(pathOrPaths) {
      const arr = Array.isArray(pathOrPaths) ? pathOrPaths : [pathOrPaths];
      if (!state.uploads) state.uploads = {};
      for (const p of arr) {
        if (typeof p !== "string") continue;
        // Accept both bare filenames (matches the Account.js / Organization.js
        // pattern of split("/").at(-1)) and full bucket/path keys.
        const key = `${bucket}/${p}`;
        if (state.uploads[key]) delete state.uploads[key];
        else {
          // Fallback: scan for any uploads whose path ends with p.
          for (const k of Object.keys(state.uploads)) {
            if (k.startsWith(`${bucket}/`) && k.endsWith(p)) delete state.uploads[k];
          }
        }
      }
      persist();
      return { data: null, error: null };
    },
  };
}

export function makeMockSupabase(state, opts = {}) {
  const persist = () => saveTrialStore(state);

  const client = {
    from(table) {
      return new QueryBuilder(state, persist, table);
    },
    rpc(name) {
      // Only delete_user is observed; in trial mode we just clear and reload.
      if (name === "delete_user") {
        clearTrialStore();
        if (typeof window !== "undefined" && opts.onSignOut) opts.onSignOut();
        return Promise.resolve({ data: null, error: null });
      }
      return Promise.resolve({ data: null, error: { message: `rpc ${name} not supported in trial` } });
    },
    auth: {
      async getSession() {
        return { data: { session: null }, error: null };
      },
      onAuthStateChange() {
        return {
          data: {
            subscription: {
              unsubscribe() {
                /* noop */
              },
            },
          },
        };
      },
      async signOut() {
        clearTrialStore();
        if (typeof window !== "undefined" && opts.onSignOut) opts.onSignOut();
        return { error: null };
      },
    },
    storage: {
      from(bucket) {
        return makeStorageBucket(state, persist, bucket);
      },
    },
  };

  return client;
}
